'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from '@/lib/auth';
import {
  getPostHogSupportClient,
  PostHogSupportError,
  type PostHogSupportSdk,
  waitForPostHogSupport,
} from '@/lib/posthog-support/client';
import {
  acknowledgedSupportTicket,
  applySelectedTicket,
  fetchPostHogSupportIdentity,
  mergeSupportMessages,
  mergeSupportTicketPage,
  putSupportTicketFirst,
  SupportIdentityRequestError,
  supportErrorKey,
  startSupportPolling,
} from '@/lib/posthog-support/hook-helpers';
import type {
  PostHogSupportStatus,
  SupportMessage,
  SupportSendInput,
  SupportTicket,
  UsePostHogSupportResult,
} from '@/lib/posthog-support/types';

export { fetchPostHogSupportIdentity } from '@/lib/posthog-support/hook-helpers';

interface SupportState {
  identityId: string | null;
  status: PostHogSupportStatus;
  tickets: SupportTicket[];
  messages: SupportMessage[];
  activeTicketId: string | null;
  error: string | null;
  isSending: boolean;
  hasMoreTickets: boolean;
}

const EMPTY_STATE: SupportState = {
  identityId: null,
  status: 'loading',
  tickets: [],
  messages: [],
  activeTicketId: null,
  error: null,
  isSending: false,
  hasMoreTickets: false,
};

export function usePostHogSupport(options?: {
  ticketId?: string;
}): UsePostHogSupportResult {
  const posthog = usePostHog();
  const { user, isLoading, isSignedOut } = useAuth();
  const authUserId = !isLoading && !isSignedOut ? (user?.userId ?? null) : null;
  const client = useMemo(
    () => getPostHogSupportClient(posthog as PostHogSupportSdk),
    [posthog]
  );
  const [state, setState] = useState<SupportState>(EMPTY_STATE);
  const stateRef = useRef(state);
  const [retryVersion, setRetryVersion] = useState(0);
  const identityGenerationRef = useRef(0);
  const operationRef = useRef(0);
  const sendingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const refreshingRef = useRef(false);
  const messageCursorRef = useRef<{
    ticketId: string;
    createdAt: string;
  } | null>(null);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  useEffect(() => {
    const generation = ++identityGenerationRef.current;
    operationRef.current += 1;
    sendingRef.current = false;
    loadingMoreRef.current = false;
    refreshingRef.current = false;
    messageCursorRef.current = null;
    if (!authUserId) {
      client.clearIdentity();
      setState({
        ...EMPTY_STATE,
        status: isLoading ? 'loading' : 'signed-out',
      });
      return;
    }
    client.clearIdentityIfDifferent(authUserId);
    const controller = new AbortController();
    setState({ ...EMPTY_STATE, identityId: authUserId });
    void (async () => {
      try {
        const identity = await fetchPostHogSupportIdentity(
          authUserId,
          controller.signal
        );
        if (identityGenerationRef.current !== generation) return;
        client.setIdentity(identity);
        const available = await waitForPostHogSupport(client, {
          signal: controller.signal,
        });
        if (identityGenerationRef.current !== generation) return;
        if (!available) throw new PostHogSupportError('providerUnavailable');

        const page = await client.listTickets();
        if (identityGenerationRef.current !== generation) return;
        const ticketId = options?.ticketId;
        if (ticketId) {
          const selected = await client.selectTicket(ticketId);
          if (identityGenerationRef.current !== generation) return;
          if (selected.latestMessageCreatedAt) {
            messageCursorRef.current = {
              ticketId,
              createdAt: selected.latestMessageCreatedAt,
            };
          }
          setState({
            identityId: authUserId,
            status: 'ready',
            tickets: applySelectedTicket(page.tickets, selected),
            messages: selected.messages,
            activeTicketId: ticketId,
            error: null,
            isSending: false,
            hasMoreTickets: page.hasMore,
          });
          return;
        }
        setState({
          ...EMPTY_STATE,
          identityId: authUserId,
          status: 'ready',
          tickets: page.tickets,
          hasMoreTickets: page.hasMore,
        });
      } catch (error) {
        if (
          controller.signal.aborted ||
          identityGenerationRef.current !== generation
        ) {
          return;
        }
        const signedOut =
          error instanceof SupportIdentityRequestError &&
          error.kind === 'signed-out';
        setState({
          ...EMPTY_STATE,
          identityId: signedOut ? null : authUserId,
          status: signedOut ? 'signed-out' : 'unavailable',
          error: signedOut ? null : supportErrorKey(error, 'failedToLoad'),
        });
      }
    })();
    return () => {
      controller.abort();
      if (identityGenerationRef.current === generation) {
        identityGenerationRef.current += 1;
      }
    };
  }, [authUserId, client, isLoading, options?.ticketId, retryVersion]);

  const isCurrent = useCallback(
    (identityId: string | null, generation: number) =>
      !!authUserId &&
      identityId === authUserId &&
      generation === identityGenerationRef.current,
    [authUserId]
  );

  const refreshReadyState = useCallback(async (): Promise<boolean> => {
    const snapshot = stateRef.current;
    if (refreshingRef.current || sendingRef.current) return true;
    if (snapshot.status !== 'ready') return true;
    const identityId = snapshot.identityId;
    const generation = identityGenerationRef.current;
    const operation = ++operationRef.current;
    if (!isCurrent(identityId, generation)) return true;
    refreshingRef.current = true;
    try {
      const page = await client.listTickets();
      const latestMessageCreatedAt =
        messageCursorRef.current?.ticketId === snapshot.activeTicketId
          ? messageCursorRef.current.createdAt
          : snapshot.messages.at(-1)?.createdAt;
      const selected = snapshot.activeTicketId
        ? await client.refreshTicket(
            snapshot.activeTicketId,
            latestMessageCreatedAt
          )
        : null;
      if (
        !isCurrent(identityId, generation) ||
        operation !== operationRef.current
      ) {
        return true;
      }
      if (selected?.latestMessageCreatedAt && snapshot.activeTicketId) {
        messageCursorRef.current = {
          ticketId: snapshot.activeTicketId,
          createdAt: selected.latestMessageCreatedAt,
        };
      }
      setState((current) => {
        const tickets = mergeSupportTicketPage(current.tickets, page.tickets);
        return {
          ...current,
          tickets: selected ? applySelectedTicket(tickets, selected) : tickets,
          messages: selected
            ? mergeSupportMessages(current.messages, selected.messages)
            : [],
          error: null,
          hasMoreTickets: page.hasMore,
        };
      });
      return true;
    } catch (error) {
      if (
        !isCurrent(identityId, generation) ||
        operation !== operationRef.current
      )
        return true;
      setState((current) => ({
        ...current,
        error: supportErrorKey(error, 'failedToLoad'),
      }));
      return false;
    } finally {
      if (generation === identityGenerationRef.current)
        refreshingRef.current = false;
    }
  }, [client, isCurrent]);

  const refresh = useCallback(async () => {
    if (stateRef.current.status === 'ready') {
      await refreshReadyState();
      return;
    }
    if (authUserId && !isLoading) {
      setState({ ...EMPTY_STATE, identityId: authUserId });
      setRetryVersion((current) => current + 1);
    }
  }, [authUserId, isLoading, refreshReadyState]);

  useEffect(() => {
    if (state.status !== 'ready') return;
    return startSupportPolling(refreshReadyState);
  }, [refreshReadyState, state.identityId, state.status]);

  const loadMoreTickets = useCallback(async () => {
    if (loadingMoreRef.current || !state.hasMoreTickets) return;
    const identityId = state.identityId;
    const generation = identityGenerationRef.current;
    if (!isCurrent(identityId, generation) || state.status !== 'ready') return;
    loadingMoreRef.current = true;
    try {
      const page = await client.listTickets(state.tickets.length);
      if (!isCurrent(identityId, generation)) return;
      setState((current) => ({
        ...current,
        tickets: [
          ...current.tickets,
          ...page.tickets.filter(
            (ticket) => !current.tickets.some((item) => item.id === ticket.id)
          ),
        ],
        error: null,
        hasMoreTickets: page.hasMore,
      }));
    } catch (error) {
      if (!isCurrent(identityId, generation)) return;
      setState((current) => ({
        ...current,
        error: supportErrorKey(error, 'failedToLoad'),
      }));
    } finally {
      if (generation === identityGenerationRef.current)
        loadingMoreRef.current = false;
    }
  }, [client, isCurrent, state]);

  const selectTicket = useCallback(
    async (ticketId: string) => {
      const identityId = state.identityId;
      const generation = identityGenerationRef.current;
      const operation = ++operationRef.current;
      if (!ticketId || !isCurrent(identityId, generation)) return;
      messageCursorRef.current = null;
      setState((current) => ({
        ...current,
        activeTicketId: ticketId,
        messages: [],
        error: null,
      }));
      try {
        const selected = await client.selectTicket(ticketId);
        if (
          !isCurrent(identityId, generation) ||
          operation !== operationRef.current
        ) {
          return;
        }
        if (selected.latestMessageCreatedAt) {
          messageCursorRef.current = {
            ticketId,
            createdAt: selected.latestMessageCreatedAt,
          };
        }
        setState((current) => ({
          ...current,
          tickets: applySelectedTicket(current.tickets, selected),
          messages: selected.messages,
          error: null,
        }));
      } catch (error) {
        if (!isCurrent(identityId, generation)) return;
        setState((current) => ({
          ...current,
          status:
            error instanceof PostHogSupportError &&
            error.code === 'providerUnavailable'
              ? 'unavailable'
              : current.status,
          error: supportErrorKey(error, 'failedToLoad'),
        }));
      }
    },
    [client, isCurrent, state.identityId]
  );

  const send = useCallback(
    async (input: SupportSendInput): Promise<SupportTicket | null> => {
      const message = input.message.trim();
      const identityId = state.identityId;
      const generation = identityGenerationRef.current;
      if (
        !message ||
        sendingRef.current ||
        !isCurrent(identityId, generation) ||
        state.status !== 'ready'
      ) {
        return null;
      }
      if (input.attachments?.length) {
        setState((current) => ({
          ...current,
          error: 'attachmentsUnsupported',
        }));
        return null;
      }

      sendingRef.current = true;
      operationRef.current += 1;
      setState((current) => ({ ...current, isSending: true, error: null }));
      const targetTicketId = state.activeTicketId;
      try {
        const response =
          input.newTicket || !targetTicketId
            ? await client.sendNew(message)
            : await client.replyToTicket(targetTicketId, message);
        if (!isCurrent(identityId, generation)) return null;
        const existing = state.tickets.find(
          (ticket) => ticket.id === response.ticket_id
        );
        const ticket = acknowledgedSupportTicket(
          response,
          message,
          existing,
          input.newTicket ? input.subject : undefined
        );
        const sentMessage: SupportMessage = {
          id: response.message_id,
          content: message,
          author: 'customer',
          createdAt: response.created_at,
        };
        messageCursorRef.current = {
          ticketId: response.ticket_id,
          createdAt: response.created_at,
        };
        setState((current) => ({
          ...current,
          tickets: putSupportTicketFirst(current.tickets, ticket),
          messages:
            targetTicketId === response.ticket_id && !input.newTicket
              ? mergeSupportMessages(current.messages, [sentMessage])
              : [sentMessage],
          activeTicketId: response.ticket_id,
          error: null,
          isSending: false,
        }));
        return ticket;
      } catch (error) {
        if (isCurrent(identityId, generation)) {
          setState((current) => ({
            ...current,
            error: supportErrorKey(error, 'failedToSend'),
            isSending: false,
          }));
        }
        return null;
      } finally {
        if (generation === identityGenerationRef.current)
          sendingRef.current = false;
      }
    },
    [client, isCurrent, state]
  );

  const startNew = useCallback(() => {
    operationRef.current += 1;
    messageCursorRef.current = null;
    setState((current) => ({
      ...current,
      activeTicketId: null,
      messages: [],
      error: null,
    }));
  }, []);

  const identityMatches = state.identityId === authUserId && !!authUserId;
  return {
    status: identityMatches
      ? state.status
      : isLoading
        ? 'loading'
        : authUserId
          ? state.status === 'signed-out'
            ? 'signed-out'
            : 'loading'
          : 'signed-out',
    tickets: identityMatches ? state.tickets : [],
    messages: identityMatches ? state.messages : [],
    activeTicketId: identityMatches ? state.activeTicketId : null,
    error: identityMatches ? state.error : null,
    isSending: identityMatches && state.isSending,
    hasMoreTickets: identityMatches && state.hasMoreTickets,
    refresh,
    loadMoreTickets,
    selectTicket,
    send,
    startNew,
  };
}
